import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { UserInfo } from '../../types';
import Button from '../Shared/Button';
import { COUNTRIES } from '../../data/countries';

const FormContainer = styled.div`
  max-width: 600px;
  margin: 40px auto;
  padding: ${theme.spacing.xlarge};
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h2`
  font-family: ${theme.fonts.heading};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.small};
  font-size: 1.8rem;
`;

const FormSubtitle = styled.p`
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.xlarge};
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.large};
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.small};
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.lightBlue};
  border-radius: 8px;
  font-size: 1rem;
  font-family: ${theme.fonts.body};
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: ${theme.spacing.small};
  display: block;
`;

const AutocompleteWrapper = styled.div`
  position: relative;
`;

const AutocompleteList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid ${theme.colors.lightBlue};
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  list-style: none;
  padding: 0;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.li<{ highlighted: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  background: ${props => props.highlighted ? theme.colors.lightBlue : 'white'};
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.lightBlue};
  }
`;

const VerifyButton = styled(Button)`
  margin-top: ${theme.spacing.medium};
  background: ${theme.colors.primary};
  
  &:hover {
    background: #0a1f3f;
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const OTPContainer = styled.div`
  margin-top: ${theme.spacing.medium};
  padding: ${theme.spacing.large};
  background: #f0f8ff;
  border: 2px solid ${theme.colors.lightBlue};
  border-radius: 8px;
`;

const OTPInput = styled(Input)`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 8px;
  font-weight: bold;
`;

const SuccessMessage = styled.span`
  color: #27ae60;
  font-size: 0.9rem;
  margin-top: ${theme.spacing.small};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:before {
    content: '✓';
    font-size: 1.2rem;
    font-weight: bold;
  }
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
  margin-top: ${theme.spacing.large};
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  &:disabled {
    color: #95a5a6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.medium};
  margin-top: ${theme.spacing.xlarge};
`;

const CancelButton = styled(Button)`
  background: #95a5a6;
  
  &:hover {
    background: #7f8c8d;
  }
`;

interface UserInfoFormProps {
  onSubmit: (userInfo: UserInfo) => void;
  onCancel: () => void;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<UserInfo>({
    name: '',
    email: '',
    company: '',
    jobTitle: '',
    country: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [countryInput, setCountryInput] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // OTP verification states
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(countryInput.toLowerCase())
  ).slice(0, 10); // Show max 10 suggestions

  const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
    'gmx.com', 'inbox.com', 'live.com', 'msn.com', 'yahoo.co.in',
    'yahoo.co.uk', 'rediffmail.com', 'googlemail.com', 'me.com'
  ];

  const isPersonalEmail = (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    return PERSONAL_EMAIL_DOMAINS.includes(domain);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEmailBlur = () => {
    const email = formData.email.trim();
    if (!email) {
      return; // Don't show error if field is empty (will be caught on submit)
    }
    
    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else if (isPersonalEmail(email)) {
      setErrors(prev => ({ 
        ...prev, 
        email: 'Please use your organizational email address (personal email domains like Gmail, Yahoo, etc. are not allowed)' 
      }));
    }
  };

  const handleSendOTP = async () => {
    const email = formData.email.trim();
    
    // Validate email before sending OTP
    if (!email) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }
    
    if (isPersonalEmail(email)) {
      setErrors(prev => ({ 
        ...prev, 
        email: 'Please use your organizational email address' 
      }));
      return;
    }

    setIsSendingOTP(true);
    setOtpError('');

    try {
      const response = await fetch('https://ai-assessment-emails.azurewebsites.net/api/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowOTPInput(true);
        setOtpSent(true);
        setErrors(prev => ({ ...prev, email: '' }));
      } else {
        setOtpError(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError('Network error. Please try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setOtpError('Please enter the OTP');
      return;
    }

    setIsVerifyingOTP(true);
    setOtpError('');

    try {
      const response = await fetch('https://ai-assessment-emails.azurewebsites.net/api/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: formData.email.trim(), 
          otp: otp.trim() 
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsEmailVerified(true);
        setShowOTPInput(false);
        setOtpError('');
      } else {
        setOtpError(data.error || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError('Network error. Please try again.');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleResendOTP = () => {
    setOtp('');
    setOtpError('');
    handleSendOTP();
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountryInput(value);
    setFormData(prev => ({ ...prev, country: value }));
    setShowCountryList(true);
    setHighlightedIndex(-1);
    // Clear error when user starts typing
    if (errors.country) {
      setErrors(prev => ({ ...prev, country: '' }));
    }
  };

  const handleCountrySelect = (countryName: string) => {
    setCountryInput(countryName);
    setFormData(prev => ({ ...prev, country: countryName }));
    setShowCountryList(false);
    setHighlightedIndex(-1);
  };

  const handleCountryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showCountryList || filteredCountries.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < filteredCountries.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleCountrySelect(filteredCountries[highlightedIndex].name);
    } else if (e.key === 'Escape') {
      setShowCountryList(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: { [key: string]: string } = {};

    // Check email verification first
    if (!isEmailVerified) {
      newErrors.email = 'Please verify your email first';
      setErrors(newErrors);
      return;
    }

    // Validate all fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (isPersonalEmail(formData.email)) {
      newErrors.email = 'Please use your organizational email address (personal email domains like Gmail, Yahoo, etc. are not allowed)';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job Title is required';
    }
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <FormContainer>
      <FormTitle>Before We Begin</FormTitle>
      <FormSubtitle>
        Please provide your information to get started with the AI Maturity Assessment.
      </FormSubtitle>
      
      <form onSubmit={handleSubmit}>
        {/* Email Field with OTP Verification */}
        <FormGroup>
          <Label htmlFor="email">Email *</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            placeholder="your.email@company.com"
            disabled={isEmailVerified}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          {isEmailVerified && <SuccessMessage>Email verified successfully</SuccessMessage>}
          
          {!isEmailVerified && !showOTPInput && (
            <VerifyButton 
              type="button" 
              onClick={handleSendOTP}
              disabled={isSendingOTP || !formData.email || !!errors.email}
            >
              {isSendingOTP ? 'Sending OTP...' : otpSent ? 'Resend OTP' : 'Send Verification Code'}
            </VerifyButton>
          )}
          
          {showOTPInput && !isEmailVerified && (
            <OTPContainer>
              <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '0.9rem' }}>
                We've sent a 6-digit code to <strong>{formData.email}</strong>
              </p>
              <OTPInput
                type="text"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ''));
                  setOtpError('');
                }}
                placeholder="000000"
              />
              {otpError && <ErrorMessage>{otpError}</ErrorMessage>}
              <VerifyButton 
                type="button" 
                onClick={handleVerifyOTP}
                disabled={isVerifyingOTP || otp.length !== 6}
              >
                {isVerifyingOTP ? 'Verifying...' : 'Verify Code'}
              </VerifyButton>
              <ResendLink 
                type="button"
                onClick={handleResendOTP}
                disabled={isSendingOTP}
              >
                {isSendingOTP ? 'Sending...' : 'Resend Code'}
              </ResendLink>
            </OTPContainer>
          )}
        </FormGroup>

        {/* Show rest of form only after email verification */}
        {isEmailVerified && (
          <>
            <FormGroup>
              <Label htmlFor="name">Name *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="company">Company *</Label>
              <Input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company name"
              />
              {errors.company && <ErrorMessage>{errors.company}</ErrorMessage>}
            </FormGroup>
          </>
        )}
        
        {!isEmailVerified && (
          <FormGroup>
            <Label htmlFor="name">Name *</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled
              style={{ opacity: 0.5 }}
            />
          </FormGroup>
        )}
        
        {!isEmailVerified && (
          <FormGroup>
            <Label htmlFor="company">Company *</Label>
            <Input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your company name"
              disabled
              style={{ opacity: 0.5 }}
            />
          </FormGroup>
        )}

        {isEmailVerified && (
          <FormGroup>
            <Label htmlFor="jobTitle">Job Title *</Label>
            <Input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Your job title"
            />
            {errors.jobTitle && <ErrorMessage>{errors.jobTitle}</ErrorMessage>}
          </FormGroup>
        )}
        
        {!isEmailVerified && (
          <FormGroup>
            <Label htmlFor="jobTitle">Job Title *</Label>
            <Input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Your job title"
              disabled
              style={{ opacity: 0.5 }}
            />
          </FormGroup>
        )}

        {isEmailVerified && (
          <FormGroup>
            <Label htmlFor="country">Country *</Label>
            <AutocompleteWrapper>
              <Input
                type="text"
                id="country"
                name="country"
                value={countryInput}
                onChange={handleCountryChange}
                onKeyDown={handleCountryKeyDown}
                onFocus={() => setShowCountryList(true)}
                onBlur={() => setTimeout(() => setShowCountryList(false), 200)}
                placeholder="Start typing your country"
                autoComplete="off"
              />
              {showCountryList && filteredCountries.length > 0 && (
                <AutocompleteList>
                  {filteredCountries.map((country, index) => (
                    <AutocompleteItem
                      key={country.name}
                      highlighted={index === highlightedIndex}
                      onClick={() => handleCountrySelect(country.name)}
                    >
                      {country.name}
                    </AutocompleteItem>
                  ))}
                </AutocompleteList>
              )}
            </AutocompleteWrapper>
            {errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
          </FormGroup>
        )}
        
        {!isEmailVerified && (
          <FormGroup>
            <Label htmlFor="country">Country *</Label>
            <Input
              type="text"
              id="country"
              placeholder="Start typing your country"
              disabled
              style={{ opacity: 0.5 }}
            />
          </FormGroup>
        )}

        {isEmailVerified && (
          <ButtonGroup>
            <CancelButton type="button" onClick={onCancel}>
              Cancel
            </CancelButton>
            <Button type="submit">
              Continue to Assessment
            </Button>
          </ButtonGroup>
        )}
      </form>
    </FormContainer>
  );
};

export default UserInfoForm;
